<template>
  <div class="comment-item">
    <div class="comment-avatar" :style="{ backgroundColor: avatarColor }">
      {{ authorInitials }}
    </div>
    <div class="comment-body">
      <div class="comment-header-info">
        <span class="comment-author">{{ authorName }}</span>
        <span v-if="isCurrentUser" class="you-badge">You</span>
        <span class="comment-date">{{ formattedDate }}<span v-if="comment.OverrideId" class="edited-text"> · edited</span></span>
      </div>
      
      <div v-if="!isEditing" class="comment-text">
        {{ comment.CommentText }}
      </div>
      
      <div v-else class="comment-edit-form">
        <textarea
          v-model="editText"
          class="edit-input"
          rows="3"
        ></textarea>
        <div class="edit-actions">
          <button @click="saveEdit" class="save-button">Save</button>
          <button @click="cancelEdit" class="cancel-button">Cancel</button>
        </div>
      </div>
      
      <div class="comment-actions">
        <button v-if="allowReplies && isTopLevelComment" @click="handleReplyClick" class="action-button">Reply</button>
        <button v-if="canEdit" @click="startEdit" class="action-button">Edit</button>
      </div>
    </div>
    
    <!-- Replies -->
    <div v-if="replies && replies.length > 0" class="replies-container">
      <div v-for="reply in replies" :key="reply.CommentId" class="reply-item">
        <CommentItem 
          :comment="reply"
          :replies="[]"
          :current-username="currentUsername"
          @reply="handleReply"
          @edit="handleEdit"
        />
      </div>
    </div>
    
    <!-- Reply form (shown when replying) -->
    <div v-if="showReplyForm" class="reply-form">
      <textarea
        v-model="replyText"
        placeholder="Write a reply..."
        class="reply-input"
        rows="2"
      ></textarea>
      <div class="reply-actions">
        <button @click="submitReply" :disabled="!replyText.trim()" class="submit-reply-button">
          Post Reply
        </button>
        <button @click="cancelReply" class="cancel-reply-button">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CommentItem',
  props: {
    comment: {
      type: Object,
      required: true
    },
    replies: {
      type: Array,
      default: () => []
    },
    currentUsername: {
      type: String,
      default: null
    },
    allowReplies: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isEditing: false,
      editText: '',
      showReplyForm: false,
      replyText: ''
    };
  },
  computed: {
    authorName() {
      try {
        const userData = JSON.parse(this.comment.UserData || '{}');
        return userData.username || 'Unknown User';
      } catch {
        return 'Unknown User';
      }
    },
    
    authorInitials() {
      const name = (this.authorName || '').trim();
      return name ? name[0].toUpperCase() : '?';
    },
    
    avatarColor() {
      const name = this.authorName || '';
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = ((hash << 5) - hash) + name.charCodeAt(i);
        hash |= 0;
      }
      const hue = Math.abs(hash % 360);
      return `hsl(${hue}, 55%, 42%)`;
    },
    
    isCurrentUser() {
      return this.currentUsername && this.authorName && 
             this.currentUsername === this.authorName;
    },
    
    formattedDate() {
      const date = new Date(this.comment.DateTimeCreated);
      return date.toLocaleString();
    },
    
    canEdit() {
      if (!this.currentUsername) return false;
      try {
        const userData = JSON.parse(this.comment.UserData || '{}');
        const authorUsername = userData.username || '';
        return authorUsername === this.currentUsername;
      } catch {
        return false;
      }
    },
    
    isTopLevelComment() {
      return !this.comment.ParentCommentId;
    }
  },
  methods: {
    handleReplyClick() {
      this.showReplyForm = true;
    },
    
    submitReply() {
      if (!this.replyText.trim()) return;
      
      this.$emit('reply', this.comment.CommentId, this.replyText.trim());
      this.replyText = '';
      this.showReplyForm = false;
    },
    
    cancelReply() {
      this.replyText = '';
      this.showReplyForm = false;
    },
    
    startEdit() {
      this.isEditing = true;
      this.editText = this.comment.CommentText;
    },
    
    saveEdit() {
      if (!this.editText.trim()) return;
      
      this.$emit('edit', this.comment.CommentId, this.editText.trim());
      this.isEditing = false;
    },
    
    cancelEdit() {
      this.isEditing = false;
      this.editText = '';
    },
    
    handleReply(commentId, replyText) {
      this.$emit('reply', commentId, replyText);
    },
    
    handleEdit(commentId, newText) {
      this.$emit('edit', commentId, newText);
    }
  }
};
</script>

<style scoped>
.comment-item {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  padding: 14px 16px;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.comment-avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: white;
  letter-spacing: 0.5px;
}

.comment-body {
  flex: 1;
  min-width: 0;
}

.comment-content {
  margin-bottom: 10px;
}

.comment-header-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.comment-author {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
}

.you-badge {
  font-size: 11px;
  font-weight: 600;
  color: #4a90e2;
  background-color: #eff6ff;
  padding: 2px 6px;
  border-radius: 4px;
}

.comment-date {
  font-size: 12px;
  color: #9ca3af;
  margin-left: auto;
}

.edited-text {
  color: #6b7280;
  font-weight: 600;
}

.comment-text {
  margin-bottom: 10px;
  line-height: 1.5;
  font-size: 14px;
  color: #374151;
  white-space: pre-wrap;
}

.comment-actions {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}

.action-button {
  background: none;
  border: none;
  color: #4a90e2;
  cursor: pointer;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 3px;
}

.action-button:hover {
  background-color: #e8f4fd;
}

.comment-edit-form {
  margin-bottom: 10px;
}

.edit-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 8px;
}

.edit-actions {
  display: flex;
  gap: 8px;
}

.save-button, .cancel-button {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.save-button {
  background-color: #4a90e2;
  color: white;
}

.save-button:hover {
  background-color: #357abd;
}

.cancel-button {
  background-color: #f0f0f0;
  color: #333;
}

.cancel-button:hover {
  background-color: #e0e0e0;
}

.replies-container {
  flex: 1 1 100%;
  margin-top: 12px;
  margin-left: 48px;
  padding-left: 16px;
  border-left: 3px solid #e5e7eb;
}

.reply-item .comment-item {
  background-color: #f9fafb;
  border-color: #e5e7eb;
}

.reply-form {
  flex: 1 1 100%;
  margin-left: 48px;
  margin-top: 12px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.reply-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 8px;
}

.reply-actions {
  display: flex;
  gap: 8px;
}

.submit-reply-button, .cancel-reply-button {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.submit-reply-button {
  background-color: #4a90e2;
  color: white;
}

.submit-reply-button:hover:not(:disabled) {
  background-color: #357abd;
}

.submit-reply-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.cancel-reply-button {
  background-color: #f0f0f0;
  color: #333;
}

.cancel-reply-button:hover {
  background-color: #e0e0e0;
}
</style>
